import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { apiErrorText } from '../api/errorText';

interface AsyncSectionProps {
  loading: boolean;
  error: Error | undefined;
  onRetry: () => void;
  children: ReactNode;
}

/// Loading and failure states in one place. A failed request shows what the server said,
/// including the remedy it sent - errors are meant to be actionable, not decorative.
export default function AsyncSection({ loading, error, onRetry, children }: AsyncSectionProps) {
  const { t } = useTranslation();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress aria-label={t('common.loading')} />
      </Box>
    );
  }

  if (error) {
    const { message, remedy } = apiErrorText(error, t);

    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={onRetry}>
            {t('common.retry')}
          </Button>
        }
      >
        <strong>{t('common.error')}</strong>
        <div>{message}</div>
        {remedy && <div>{remedy}</div>}
      </Alert>
    );
  }

  return <>{children}</>;
}
